/**
 * @fileoverview Shared {{advField:...}} token substitution helpers for the document-render package.
 * @module document-render/advancedTokens
 *
 * Responsibilities: - Replace {{advField:fieldId}} tokens in HTML strings with resolved values.
 *                   - Replace tokens across all static-band elements in a DocumentLayout.
 * Dependencies:     - ./documentConfig (ResolvedAdvancedField)
 *                   - ./documentLayout (DocumentLayout, Band)
 * Architecture:     - Pure functions — no I/O, no side effects.
 *                     Consumed by renderSections.ts (renderBandToHtml) and the worker transformer
 *                     so both paths share one implementation (G-DRY-RENDER).
 * Notes:            - Unmatched tokens are left unchanged so authoring mistakes are visible.
 */

import type { ResolvedAdvancedField } from './documentConfig';
import type { DocumentLayout, Band } from './documentLayout';

/**
 * Replace `{{advField:fieldId}}` tokens in an HTML string with the already-resolved
 * value for that field (e.g. ☑ or ☐ for checkbox fields).
 * Unmatched tokens are left unchanged.
 *
 * @param html           Raw HTML string (may contain zero or more tokens).
 * @param resolvedFields Array of resolved advanced fields for the current document.
 * @returns HTML with every matched token replaced by its resolved value.
 */
export function resolveAdvancedTokensInHtml(
  html: string,
  resolvedFields: ResolvedAdvancedField[],
): string {
  if (!html || resolvedFields.length === 0) return html;
  const valueMap = new Map<string, string>(resolvedFields.map((f) => [f.id, f.value]));
  return html.replace(/\{\{advField:([^}]+)\}\}/g, (_match, fieldId: string) => {
    const trimmed = fieldId.trim();
    return valueMap.has(trimmed) ? (valueMap.get(trimmed) ?? '') : _match;
  });
}

/**
 * Apply `{{advField:...}}` token substitution to every static-band element's HTML
 * in a DocumentLayout (richText, callout, image bands). Dynamic bands are skipped
 * since they render from resolved data directly.
 *
 * This is the Canvas-layout counterpart to the Sections-path body-paragraph substitution,
 * ensuring preview and PDF output are identical (G-DRY-RENDER parity).
 *
 * @param layout         DocumentLayout whose band element HTML may contain tokens.
 * @param resolvedFields Array of resolved advanced fields for the current document.
 * @returns A new DocumentLayout with tokens substituted; original is not mutated.
 */
export function resolveAdvancedTokensInLayout(
  layout: DocumentLayout,
  resolvedFields: ResolvedAdvancedField[],
): DocumentLayout {
  if (!resolvedFields.length) return layout;

  const resolveBand = (band: Band): Band => {
    if (band.kind !== 'richText' && band.kind !== 'callout' && band.kind !== 'image') return band;
    return {
      ...band,
      elements: band.elements.map((el) => ({
        ...el,
        html: el.html ? resolveAdvancedTokensInHtml(el.html, resolvedFields) : el.html,
      })),
    };
  };

  return {
    ...layout,
    header: layout.header ? resolveBand(layout.header) : layout.header,
    footer: layout.footer ? resolveBand(layout.footer) : layout.footer,
    body: layout.body.map(resolveBand),
  };
}
